import { ButtonPrimary, ButtonTertiary } from "@nova/component-btn"
import { FormTextInput } from "@nova/component-form-text-input"
import { HighPriorityNovaModalContainer } from "@nova/component-nova-modal-container"
import { FileExtension } from "@nova/constants"
import { DropBoxV2 } from "panels/content/top-bar/TODO-put-in-package/dropbox/DropBoxV2"
import { FunctionComponent, useState } from "react"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"
import { extractTakCertificates, TAKAuthorizationError, useAuthorizeTAK } from "./useAuthorizeTAK"
import { setGlobalNotificationContext } from "user/profile/slice"
import { GlobalNotificationStatus } from "@nova/component-global-notification"
import * as Icons from "@nova/component-icons"
import { mapExtensionsToMimeType } from "features/job-management/uploader/constants"
import { AlertContainer } from "@nova/component-alert-container"
import { hideGlobalModal } from "runlevel/actions"

const TAKAuthModal: FunctionComponent = () => {
    const [serverUrl, setServerUrl] = useState<string>("https://")
    const [p12, setP12] = useState<File>()
    const [password, setPassword] = useState<string>("")
    const [showPassword, setShowPassword] = useState(false)
    const [error, setError] = useState<TAKAuthorizationError>()
    const [showCAWarning, setShowCAWarning] = useState<boolean>(false)
    const [port, setPort] = useState("8443")

    const workspaceId = useAppSelector(selectActiveWorkspaceId())

    const dispatch = useAppDispatch()

    const handleAuthCreate = () => {
        dispatch(
            setGlobalNotificationContext({
                message: `Connected to TAK.`,
                status: GlobalNotificationStatus.success,
                timeoutInMs: 3000,
            })
        )
        dispatch(hideGlobalModal())
    }

    const handleAuthError = (e: TAKAuthorizationError) => {
        if (e.cause === "network") {
            dispatch(
                setGlobalNotificationContext({
                    message: `Failed to connect to TAK.`,
                    status: GlobalNotificationStatus.error,
                    timeoutInMs: 3000,
                })
            )
            return
        }
        setError(e)
    }

    const { authorizeTak, isMutating } = useAuthorizeTAK(handleAuthCreate, handleAuthError)

    if (p12)
        extractTakCertificates(p12, password).then(({ ca }) => {
            if (!ca) setShowCAWarning(true)
        })

    const handleFileUpload = async (files: File[]) => {
        const file = files.at(0)
        if (!file) return

        if (file.type !== mapExtensionsToMimeType["p12"]) return

        setP12(file)
    }

    const handleAuthClick = () => {
        const serverWithPort = port ? serverUrl + `:${port}` : serverUrl
        if (p12 && workspaceId)
            authorizeTak({ p12, serverUrl: serverWithPort, workspaceId, password })
    }

    return (
        <HighPriorityNovaModalContainer
            title="Connect to TAK"
            className="w-[30rem]"
            bottomBar={
                <div className="w-full flex justify-end items-center gap-2">
                    <ButtonTertiary title="Cancel" onClick={() => dispatch(hideGlobalModal())} />
                    <ButtonPrimary
                        title="Connect"
                        isLoading={isMutating}
                        onClick={handleAuthClick}
                        RightIcon={Icons.ArrowRight}
                        disabled={!serverUrl || !p12}
                    />
                </div>
            }
            closeModal={() => dispatch(hideGlobalModal())}
        >
            <div className="flex flex-col gap-6">
                <div className="flex gap-2">
                    <FormTextInput
                        className="flex-1"
                        id="tak-server-url"
                        label="Server URL"
                        name="tak-server-url"
                        value={serverUrl}
                        onChange={(e) => setServerUrl(e.target.value)}
                        type="url"
                        rightIconFocusedOnly
                        rightIcon={
                            <Icons.XClose
                                className="text-gray-300 cursor-pointer"
                                onClick={() => setServerUrl("")}
                            />
                        }
                    />
                    <FormTextInput
                        className="max-w-[16%]"
                        id="tak-server-port"
                        label="Port"
                        name="tak-server-port"
                        value={port}
                        onChange={(e) => setPort(e.target.value)}
                    />
                </div>
                <FormTextInput
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    id="tak-p12-password"
                    label="Password (if required)"
                    name="tak-p12-password"
                    type={showPassword ? "text" : "password"}
                    error={error?.cause === "password" ? error.message : undefined}
                    rightIcon={
                        <span
                            onClick={() => setShowPassword((prev) => !prev)}
                            className="text-gray-300 cursor-pointer"
                        >
                            {showPassword ? <Icons.VisibilityEye /> : <Icons.VisibilityEyeOff />}
                        </span>
                    }
                />

                {p12 ? (
                    <UploadedFile
                        fileName={p12.name}
                        onClick={() => setP12(undefined)}
                        error={error?.cause === "p12" ? error.message : undefined}
                    />
                ) : (
                    <div className="flex flex-col gap-2">
                        <h5 className="text-gray-100">Security Key</h5>
                        <DropBoxV2
                            allowedFileExtensions={[FileExtension.p12]}
                            isProcessing={false}
                            onNewItems={handleFileUpload}
                            title="Upload File"
                        />
                    </div>
                )}
                {showCAWarning && (
                    <AlertContainer
                        title="CA Certification"
                        body="Connecting with this p12 will skip CA validation."
                        type="warning"
                    />
                )}
            </div>
        </HighPriorityNovaModalContainer>
    )
}

interface UploadedFileProps {
    fileName: string
    onClick: () => void
    error?: string
}
const UploadedFile: FunctionComponent<UploadedFileProps> = ({ fileName, onClick, error }) => (
    <div className="flex flex-col gap-1">
        <div className="flex gap-2 items-center">
            <h5>{fileName}</h5>
            <Icons.Trash
                className="text-gray-100 hover:text-red-500 cursor-pointer"
                onClick={onClick}
            />
        </div>
        {error && <h5 className="text-red-500">{error}</h5>}
    </div>
)

export { TAKAuthModal }
